import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link2, Pencil, Plus, Search, Trash2, Unlink } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { api, unwrapPage } from '@/api/client';
import { useCan } from '@/auth/queries';
import { Cargando, ErrorCarga, Vacio } from '@/components/Estados';
import type { PreguntaItem } from '@/features/preguntas/queries';
import { fechaLarga } from '@/lib/formato';

import { ElegirLicencias } from '../ElegirLicencias';
import { useAsignar, useResumenBiblioteca } from '../queries';
import {
  useCasosLigados,
  useCasosMutaciones,
  useNotas,
  useNotasMutaciones,
  type Destino,
} from './queries';

/** Notas del autor de un contenido (no las ve el alumno como notas propias: son del equipo). */
export function NotasContenido({ destino }: { destino: Destino }) {
  const editable = useCan('contenido', 'editar');
  const q = useNotas(destino);
  const { crear, editar, borrar } = useNotasMutaciones(destino);
  const [nueva, setNueva] = useState('');
  const [editando, setEditando] = useState<{ id: number; body: string } | null>(null);

  return (
    <div className="space-y-4">
      {editable && (
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (nueva.trim()) crear.mutate(nueva.trim(), { onSuccess: () => setNueva('') });
          }}
        >
          <textarea
            className="input-vidrio min-h-20"
            placeholder="Escribe una nota para este contenido…"
            aria-label="Nueva nota"
            value={nueva}
            maxLength={5000}
            onChange={(e) => setNueva(e.target.value)}
          />
          <div className="flex justify-end">
            <button
              type="submit"
              className="btn-cs btn-primario"
              disabled={!nueva.trim() || crear.isPending}
            >
              <Plus size={16} aria-hidden /> Agregar nota
            </button>
          </div>
        </form>
      )}
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data?.length === 0 && <Vacio titulo="Sin notas" />}
      <ul className="space-y-2">
        {q.data?.map((n) => (
          <li key={n.id} className="rounded-lg border border-[var(--separador)] p-3 text-sm">
            {editando?.id === n.id ? (
              <div className="space-y-2">
                <textarea
                  className="input-vidrio min-h-20"
                  aria-label="Editar nota"
                  value={editando.body}
                  maxLength={5000}
                  onChange={(e) => setEditando({ id: n.id, body: e.target.value })}
                />
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="btn-cs btn-contorno"
                    onClick={() => setEditando(null)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn-cs btn-primario"
                    disabled={!editando.body.trim() || editar.isPending}
                    onClick={() =>
                      editar.mutate(
                        { id: n.id, body: editando.body.trim() },
                        { onSuccess: () => setEditando(null) },
                      )
                    }
                  >
                    Guardar
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <p className="flex-1 whitespace-pre-wrap">{n.body}</p>
                <span className="shrink-0 text-xs text-texto-3">
                  {n.updatedAt && fechaLarga(n.updatedAt)}
                </span>
                {editable && (
                  <>
                    <button
                      type="button"
                      className="text-texto-3 hover:text-texto"
                      aria-label="Editar nota"
                      onClick={() => setEditando({ id: n.id, body: n.body })}
                    >
                      <Pencil size={15} aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="text-texto-3 hover:text-rojo-texto"
                      aria-label="Borrar nota"
                      disabled={borrar.isPending}
                      onClick={() => borrar.mutate(n.id)}
                    >
                      <Trash2 size={15} aria-hidden />
                    </button>
                  </>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Buscador del banco de casos (requiere ver la sección Preguntas; si no, se liga por número). */
function BuscarCaso({ onElegir, ocupado }: { onElegir: (id: number) => void; ocupado: boolean }) {
  const puedeBuscar = useCan('preguntas', 'ver');
  const [texto, setTexto] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const q = useQuery({
    queryKey: ['preguntas', 'buscar-ligar', busqueda],
    queryFn: () =>
      unwrapPage<PreguntaItem[]>(
        api.GET('/api/v1/admin/questions', { params: { query: { q: busqueda, pageSize: 10 } } }),
      ),
    enabled: puedeBuscar && busqueda.length >= 2,
    placeholderData: keepPreviousData,
  });
  const numero = /^\d+$/.test(texto.trim()) ? Number(texto.trim()) : null;

  return (
    <div className="space-y-2">
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (puedeBuscar) setBusqueda(texto.trim());
          else if (numero) onElegir(numero);
        }}
      >
        <input
          className="input-vidrio flex-1"
          aria-label="Buscar caso clínico"
          placeholder={
            puedeBuscar ? 'Busca por texto o escribe el número del caso' : 'Número del caso'
          }
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
        />
        {puedeBuscar && (
          <button type="submit" className="btn-cs btn-vidrio">
            <Search size={16} aria-hidden /> Buscar
          </button>
        )}
        {numero !== null && (
          <button
            type="button"
            className="btn-cs btn-primario"
            disabled={ocupado}
            onClick={() => onElegir(numero)}
          >
            <Link2 size={16} aria-hidden /> Ligar #{numero}
          </button>
        )}
      </form>
      {q.data && q.data.data.length === 0 && (
        <p className="text-sm text-texto-3">Sin resultados.</p>
      )}
      {q.data && q.data.data.length > 0 && (
        <ul className="max-h-60 space-y-1 overflow-y-auto">
          {q.data.data.map((p) => (
            <li key={p.id} className="flex items-start gap-2 text-sm">
              <button
                type="button"
                className="btn-cs btn-vidrio shrink-0 px-2"
                aria-label={`Ligar caso ${p.id}`}
                disabled={ocupado}
                onClick={() => onElegir(p.id)}
              >
                <Link2 size={14} aria-hidden />
              </button>
              <span>
                <span className="font-semibold">#{p.id}</span> {p.prompt.slice(0, 160)}
                <span className="block text-xs text-texto-3">
                  {p.specialtyName ?? 'Sin especialidad'}
                  {p.isEnglish && ' · inglés'}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Casos clínicos que el alumno resuelve al ver este contenido. */
export function CasosLigados({ destino }: { destino: Destino }) {
  const editable = useCan('contenido', 'editar');
  const q = useCasosLigados(destino);
  const { ligar, desligar } = useCasosMutaciones(destino);
  return (
    <div className="space-y-4">
      {editable && <BuscarCaso ocupado={ligar.isPending} onElegir={(id) => ligar.mutate(id)} />}
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data?.length === 0 && <Vacio titulo="Sin casos ligados" />}
      {q.data && q.data.length > 0 && (
        <ul className="space-y-2">
          {q.data.map((c) => (
            <li
              key={c.id}
              className="flex items-start gap-3 rounded-lg border border-[var(--separador)] p-3 text-sm"
            >
              <div className="flex-1">
                <Link to={`/preguntas/${c.questionId}`} className="font-semibold hover:underline">
                  #{c.questionId}
                </Link>{' '}
                {c.prompt}
                <p className="text-xs text-texto-3">
                  {c.specialtyName ?? 'Sin especialidad'}
                  {c.isEnglish && ' · inglés'}
                </p>
              </div>
              {editable && (
                <button
                  type="button"
                  className="btn-cs btn-vidrio px-2"
                  aria-label={`Desligar caso ${c.questionId}`}
                  disabled={desligar.isPending}
                  onClick={() => desligar.mutate(c.id)}
                >
                  <Unlink size={14} aria-hidden />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Licencias que incluyen el elemento; cada casilla se guarda al momento. */
export function LicenciasElemento({
  libraryItemId,
  asignadas,
}: {
  libraryItemId: number;
  asignadas: number[];
}) {
  const editable = useCan('contenido', 'editar');
  const resumen = useResumenBiblioteca();
  const asignar = useAsignar();
  const elegidas = new Set(asignadas);
  return (
    <div>
      <p className="mb-3 text-xs text-texto-3">
        Cada casilla se guarda al momento. {elegidas.size} licencia(s) con este elemento.
      </p>
      {resumen.isPending && <Cargando />}
      {resumen.data && (
        <ElegirLicencias
          licencias={resumen.data.memberships}
          elegidas={elegidas}
          deshabilitado={!editable || asignar.isPending}
          onCambio={(id, marcada) =>
            asignar.mutate({
              action: marcada ? 'add' : 'remove',
              libraryItemIds: [libraryItemId],
              membershipIds: [id],
            })
          }
        />
      )}
    </div>
  );
}
